import { ApolloError, gql, useMutation } from '@apollo/client';
import {
  DashboardViewFragment,
  DashboardViewFragmentDoc,
  DuplicateAndUpdateDashboardInput,
  ListDashboardsDocument,
  Mutation,
} from 'generated/graphql';
import { useCallback } from 'react';
import { useGetUser } from 'v2/core/services/user';

const DUPLICATE_AND_UPDATE_DASHBOARD_GQL = gql`
  mutation duplicateAndUpdateDashboard(
    $tenantID: ID!
    $userID: ID!
    $dashboardID: ID!
    $shouldDuplicateAnalyses: Boolean
    $updateParams: DuplicateAndUpdateDashboardParams
  ) {
    duplicateAndUpdateDashboard(
      request: {
        tenantID: $tenantID
        userID: $userID
        dashboardID: $dashboardID
        shouldDuplicateAnalyses: $shouldDuplicateAnalyses
        updateParams: $updateParams
      }
    ) {
      dashboard {
        ...DashboardView
      }
    }
  }
  ${DashboardViewFragmentDoc}
`;

interface useDuplicateAndUpdateDashboardParams {
  onCompleted?: (dashboard?: DashboardViewFragment) => void;
  onError?: (error: ApolloError) => void;
}

interface duplicateAndUpdateDashboardParams {
  duplicateAndUpdateDashboardInput: Omit<
    DuplicateAndUpdateDashboardInput,
    'tenantID' | 'userID'
  >;
  shouldRefetchAnalyses?: boolean;
}

export function useDuplicateAndUpdateDashboard({
  onCompleted,
  onError,
}: useDuplicateAndUpdateDashboardParams = {}) {
  const {
    tenant: { id: tenantID },
    id: userID,
  } = useGetUser();

  const [duplicateAndUpdateDashboardMutation, { loading, error }] = useMutation<
    Mutation,
    DuplicateAndUpdateDashboardInput
  >(DUPLICATE_AND_UPDATE_DASHBOARD_GQL, {
    onError,
    onCompleted: (data) => {
      onCompleted?.(
        // ##TODO, find a way to remove type casting
        data.duplicateAndUpdateDashboard?.dashboard as
          | DashboardViewFragment
          | undefined,
      );
    },
  });

  const duplicateAndUpdateDashboard = useCallback(
    async ({
      duplicateAndUpdateDashboardInput,
      shouldRefetchAnalyses,
    }: duplicateAndUpdateDashboardParams) => {
      const resp = await duplicateAndUpdateDashboardMutation({
        variables: {
          tenantID,
          userID,
          ...duplicateAndUpdateDashboardInput,
        },
        refetchQueries: shouldRefetchAnalyses
          ? [ListDashboardsDocument]
          : undefined,
      });

      // ##TODO, find a way to remove type casting
      return resp.data?.duplicateAndUpdateDashboard?.dashboard as
        | DashboardViewFragment
        | undefined;
    },
    [duplicateAndUpdateDashboardMutation, tenantID, userID],
  );

  return [
    duplicateAndUpdateDashboard,
    {
      loading,
      error,
    },
  ] as const;
}
